import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { css } from "@emotion/css";
import Badge from "./Badge";
import { COLOR_BG_PRIMARY, COLOR_BORDER_PRIMARY } from "./theme";
import { useServices } from "./ServiceProvider";
const getQuickSearchStyle = (isVisible) => {
    const quickSearchWidth = Math.min(800, Math.floor(document.documentElement.clientWidth * 0.8));
    return css `
    ${!isVisible ? 'display: none;' : ''}
    width: ${quickSearchWidth}px;
    position: absolute;
    top: 2em;
    left: ${(document.documentElement.clientWidth - quickSearchWidth) / 2}px;
    z-index: 100;

    background-color: ${COLOR_BG_PRIMARY};
    border: 2px solid ${COLOR_BORDER_PRIMARY};
    border-radius: 0.25em;
    padding: 1em;
  `;
};
const getNewResourceViewModel = (dispatch, WorkspaceService, resourceTypes) => ({
    placeholder: 'Create a new...',
    options: Object.values(resourceTypes).map((resourceType) => ({
        label: resourceType.name,
        id: resourceType.id,
        icon: resourceType.icon,
        run: () => dispatch(WorkspaceService.createNew(resourceType)),
    })),
});
const getSearchResourceTypesViewModel = (dispatch, WorkspaceService, project, resourceTypes) => ({
    placeholder: 'Search resources by name',
    options: Object.values(project.resources).map(({ id, name, typeID: type }) => ({
        label: name,
        id,
        icon: resourceTypes[type]?.icon,
        run: () => dispatch(WorkspaceService.openResource(id)),
    })),
});
const QuickSearch = () => {
    const { ProjectService, WorkspaceService } = useServices();
    const isVisible = useSelector(WorkspaceService.selectors.isQuicksearchVisible);
    const mode = useSelector(WorkspaceService.selectors.getQuicksearchMode);
    const project = useSelector(ProjectService.selectors.getProject);
    const resourceTypes = useSelector(ProjectService.selectors.getResourceTypes);
    const dispatch = useDispatch();
    const parentRef = useRef(null);
    const inputRef = useRef(null);
    useEffect(() => {
        const keydownListener = (event) => {
            if ((event.key === 'e' || event.key === 'E') && event.ctrlKey) {
                event.stopPropagation();
                event.preventDefault();
                dispatch(WorkspaceService.actions.toggleQuicksearch());
            }
            if (event.key === 'n' && event.ctrlKey && event.altKey) {
                event.stopPropagation();
                event.preventDefault();
                dispatch(WorkspaceService.actions.openNewResourcePicker());
                // throw new Error('Some error');
            }
        };
        const focusoutListener = () => {
            dispatch(WorkspaceService.actions.hideQuicksearch());
        };
        const escapeListener = (event) => {
            if (event.key === 'Escape') {
                dispatch(WorkspaceService.actions.hideQuicksearch());
            }
        };
        window.addEventListener('keydown', keydownListener);
        parentRef.current?.addEventListener('focusout', focusoutListener);
        parentRef.current?.addEventListener('keydown', escapeListener);
        return () => {
            window.removeEventListener('keydown', keydownListener);
            parentRef.current?.removeEventListener('focusout', focusoutListener);
            parentRef.current?.removeEventListener('keydown', escapeListener);
        };
    });
    useEffect(() => {
        if (isVisible) {
            inputRef.current?.focus();
        }
    }, [isVisible]);
    const viewModel = mode === 'new-resource-picker'
        ? getNewResourceViewModel(dispatch, WorkspaceService, resourceTypes)
        : getSearchResourceTypesViewModel(dispatch, WorkspaceService, project, resourceTypes);
    return isVisible ? (React.createElement("div", { id: "quicksearch", ref: parentRef, className: getQuickSearchStyle(isVisible) },
        React.createElement(Autocomplete, { autoFocus: true, autoHighlight: true, openOnFocus: true, onChange: (_, value) => {
                dispatch(WorkspaceService.actions.hideQuicksearch());
                value?.run && value.run();
            }, options: viewModel.options, renderInput: (params) => (React.createElement(TextField, { ...params, "test-id": "search-input", inputRef: inputRef, placeholder: viewModel.placeholder })), renderOption: ({ key, ...props }, { id, label, icon }) => (React.createElement("li", { id: id, "test-id": id, className: css(`display: inline-block;`), ...props, key: key },
                React.createElement(Badge, { icon: icon, iconSize: "small", label: label }))), slotProps: {
                popper: {
                    id: 'quicksearch-picker'
                }
            }, value: null }))) : null;
};
export default QuickSearch;
//# sourceMappingURL=QuickSearch.js.map